// Этажи корпуса (встреча 22.10.2026): нетиповые этажи — подземный, технический, первый, кровля — со своим
// шаблоном работ; добавить подземный этаж снизу или этаж сверху; уже сделанные работы — задним числом как 100%.

import { useState } from "react";
import { fmtDate, localISODate } from "../../shared/format";
import { Button, Drawer, Field, Input, Loading, Modal, Select, useAsync, useToast } from "../../shared/ui";
import { api, FOUNDATION_FLOOR, floorName, floorShort, isStage, PREP_FLOOR } from "../ctx";

export const FLOOR_KIND: Record<string, string> = {
  underground: "подземный", first: "первый", typical: "типовой", tech: "технический", roof: "кровля",
};
const STAGE_KIND: Record<string, string> = { prep: "подготовительный этап", foundation: "фундаментная плита" };
const STATUS: Record<string, string> = { not_started: "не начат", in_progress: "идёт", done: "выполнен" };

type FloorRow = { id: string; number: number; kind: string; template_id: string | null; cycle: number | null; status: string | null };

export function FloorsDrawer({ building, onClose, onChanged }: { building: any; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const f = useAsync<FloorRow[]>(() => api.get(`/buildings/${building.id}/floors`), [building.id]);
  const tpl = useAsync<any[]>(() => api.get("/cycle-templates", { project_id: building.project_id }), [building.id]);
  const [backfill, setBackfill] = useState<FloorRow | null>(null);
  const [addStage, setAddStage] = useState<"prep" | "foundation" | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true);
    try { await fn(); toast.ok(ok); f.reload(); onChanged(); } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  const rows = f.data || [];
  const real = rows.filter((r) => !isStage(r.number));
  const lowest = Math.min(0, ...real.map((r) => r.number));
  const started = rows.some((r) => r.status && r.status !== "not_started");
  const highest = Math.max(0, ...real.map((r) => r.number));
  const tplOf = (stage: string) => (tpl.data || []).filter((t) => (t.stage || "floor") === stage);
  const tplName = (id: string | null) => (id ? tpl.data?.find((t) => t.id === id)?.name : null);
  return (
    <Drawer title={`Этажи: ${building.name}`} onClose={onClose} footer={<Button onClick={onClose}>Готово</Button>}>
      <div className="stack">
        <p className="small muted" style={{ margin: 0 }}>
          Нетиповой этаж (подземный, технический, первый) может идти по своему шаблону работ — создайте его на экране «Шаблоны циклов»
          (например, копией типового) и выберите здесь. Шаблон меняется, пока на этаже нет факта; план перестраивается, прежняя версия сохраняется в «Истории».
        </p>
        <div className="row">
          <Button disabled={busy} onClick={() => run(() => api.post(`/buildings/${building.id}/floors`, { number: lowest - 1, kind: "underground" }), `Добавлен этаж ${lowest - 1}, план пересчитан`)}>+ подземный этаж снизу</Button>
          <Button disabled={busy} onClick={() => run(() => api.post(`/buildings/${building.id}/floors`, { number: highest + 1 }), `Добавлен этаж ${highest + 1}, план пересчитан`)}>+ этаж сверху</Button>
          {highest > 0 && <Button variant="ghost" disabled={busy} onClick={() => run(() => api.del(`/buildings/${building.id}/floors/${highest}`), "Верхний этаж убран")}>− верхний этаж</Button>}
          {!started && !rows.some((r) => r.number === PREP_FLOOR) && <Button disabled={busy} onClick={() => setAddStage("prep")}>+ подготовительный этап</Button>}
          {!started && !rows.some((r) => r.number === FOUNDATION_FLOOR) && <Button disabled={busy} onClick={() => setAddStage("foundation")}>+ фундаментная плита</Button>}
        </div>
        {!f.data || !tpl.data ? <Loading error={f.error || tpl.error} /> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th className="num">Этаж</th><th>Тип</th><th>Шаблон работ</th><th>Состояние</th><th /></tr></thead>
              <tbody>
                {[...rows].reverse().map((r) => {
                  const started = r.status && r.status !== "not_started";
                  const stage = isStage(r.number);
                  return (
                    <tr key={r.id}>
                      <td className="num"><b>{stage ? floorShort(r.number) : r.number}</b></td>
                      <td>
                        {stage ? <span>{STAGE_KIND[r.kind] || r.kind}</span> : (
                          <Select aria-label={`Тип этажа ${r.number}`} value={r.kind} style={{ width: 150 }} disabled={busy}
                            onChange={(e) => run(() => api.patch(`/buildings/${building.id}/floors/${r.number}`, { kind: e.target.value }), `Этаж ${r.number}: ${FLOOR_KIND[e.target.value]}`)}
                            options={Object.entries(FLOOR_KIND).map(([value, label]) => ({ value, label }))} />
                        )}
                      </td>
                      <td>
                        {started ? <span className="small">{tplName(r.template_id) || "как у корпуса"}</span> : (
                          <Select aria-label={`Шаблон: ${floorName(r.number, false)}`} value={r.template_id || ""} style={{ width: 240 }} disabled={busy}
                            onChange={(e) => run(() => api.patch(`/buildings/${building.id}/floors/${r.number}`, e.target.value ? { template_id: e.target.value } : { use_default_template: true }),
                              `${floorName(r.number)}: план перестроен по шаблону`)}
                            options={stage ? tplOf(r.kind).map((t) => ({ value: t.id, label: t.name })) : [{ value: "", label: "как у корпуса" }, ...tplOf("floor").map((t) => ({ value: t.id, label: t.name }))]} />
                        )}
                      </td>
                      <td className="small">{STATUS[r.status || ""] || "—"}</td>
                      <td>{r.status !== "done" && <Button size="small" onClick={() => setBackfill(r)}>Внести сделанное</Button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {addStage && (
        <AddStageModal stage={addStage} templates={tplOf(addStage)} onClose={() => setAddStage(null)}
          onSave={(template_id) => run(() => api.post(`/buildings/${building.id}/floors`, { number: addStage === "prep" ? PREP_FLOOR : FOUNDATION_FLOOR, kind: addStage, template_id }),
            `${floorName(addStage === "prep" ? PREP_FLOOR : FOUNDATION_FLOOR)} добавлен, план пересчитан`).then(() => setAddStage(null))} />
      )}
      {backfill && <BackfillModal building={building} floor={backfill} onClose={() => setBackfill(null)} onDone={() => { setBackfill(null); f.reload(); onChanged(); }} />}
    </Drawer>
  );
}

/** Уже выполненные работы этажа — задним числом как 100% (фундамент сделан, стены подземного этажа идут). */
function BackfillModal({ building, floor, onClose, onDone }: { building: any; floor: FloorRow; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const today = localISODate();
  const [v, setV] = useState({ group: isStage(floor.number) ? "all" : "vertical", date_from: "", date_to: today, comment: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/buildings/${building.id}/backfill`, {
        floor: floor.number, group: v.group, date_from: v.date_from || null, date_to: v.date_to, comment: v.comment.trim() || null,
      });
      toast.ok(r.tasks ? `Внесено работ: ${r.tasks}, выполнены ${fmtDate(v.date_to)}` : "Эти работы уже отмечены выполненными");
      onDone();
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  return (
    <Modal title={`${floorName(floor.number)}: внести сделанное задним числом`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={busy || !v.date_to} onClick={save}>Отметить выполненными</Button>
    </>}>
      <div className="form-grid">
        {!isStage(floor.number) && (
          <Field label="Какие работы" wide>
            <Select value={v.group} onChange={(e) => setV({ ...v, group: e.target.value })}
              options={[{ value: "vertical", label: "Вертикаль: стены и колонны" }, { value: "slab", label: "Перекрытие" }, { value: "all", label: "Все работы этажа" }]} />
          </Field>
        )}
        <Field label="Начали" hint="Необязательно"><Input type="date" max={today} value={v.date_from} onChange={(e) => setV({ ...v, date_from: e.target.value })} /></Field>
        <Field label="Закончили"><Input type="date" max={today} value={v.date_to} onChange={(e) => setV({ ...v, date_to: e.target.value })} /></Field>
        <Field label="Комментарий" wide hint="Попадёт в отчёты за эти дни и в историю"><Input value={v.comment} onChange={(e) => setV({ ...v, comment: e.target.value })} placeholder="Внесено задним числом" /></Field>
      </div>
      <p className="small muted">Работы будут отмечены выполненными на 100% датой окончания; в день начала — отметка «начато». План, прогноз и отставание пересчитаются.</p>
    </Modal>
  );
}

/** Добавить этап до этажей в корпус, где работы ещё не начаты. */
function AddStageModal({ stage, templates, onClose, onSave }: { stage: "prep" | "foundation"; templates: any[]; onClose: () => void; onSave: (template_id: string) => Promise<unknown> }) {
  const [tid, setTid] = useState(templates[0]?.id || "");
  const name = stage === "prep" ? "подготовительный этап" : "фундаментная плита";
  return (
    <Modal title={`Добавить: ${name}`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={!tid} onClick={() => onSave(tid)}>Добавить</Button>
    </>}>
      {templates.length === 0 ? <p className="muted">Шаблона нет — создайте его: «Шаблоны циклов» → «Новый шаблон» → «Для чего: {name}».</p> : (
        <Field label="Шаблон работ"><Select value={tid} onChange={(e) => setTid(e.target.value)} options={templates.map((t) => ({ value: t.id, label: t.name }))} /></Field>
      )}
      <p className="small muted">Этап встанет в план первым — раньше подземных этажей; даты этажей сдвинутся на его длительность. Прежняя версия плана сохранится в «Истории».</p>
    </Modal>
  );
}
